import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BasicInput, NumberInput, SimplePopover, Typography, palette } from '@dev/ee-components';
import { Link } from '@material-ui/core';
import { ContactFieldType, ContactFieldTypes } from '@dev/ee-api/ee-api-models';
import { validateCustomFieldName } from '@/components/Contacts/CustomFields/tools';
import { useDataTypes } from '@/components/Contacts/CustomFields/hooks/useDataTypes';
import { links } from '@/services/dictionaries/links';
import { useSecurity } from '@/services/hooks/useSecurity';
import { getCustomFieldsStore } from '@/store/customFields/actions';
import { CUSTOM_FIELD_MAX_FIELD_LENGTH } from '@/services/constants';
import { useFieldTypes } from '@/components/Contacts/CustomFields/hooks/useFieldTypes';
import { CustomFieldTypes } from '@/services/dictionaries/CustomFieldTypes';
import { AddCustomFieldModalProps } from './types';
import { getCustomFieldTypeOptions } from './tools';
import { CreateCustomFieldModalType } from '../../hooks/useCreateFieldModalParams';
import { InfoWrapper, InputsWrapper } from './styles';

export const AddCustomFieldModal = ({ onChange }: AddCustomFieldModalProps): JSX.Element => {
    const { t } = useTranslation();
    const [field, setField] = useState<CreateCustomFieldModalType>({
        isLengthError: false,
        isNameError: false,
        isLengthSetable: true,
        isCustom: true,
        type: CustomFieldTypes.Custom,
        ...getCustomFieldTypeOptions(CustomFieldTypes.Custom)
    });
    const [nameError, setNameError] = useState<string>();
    const { collection, nameFieldBackendError } = useSelector(getCustomFieldsStore);
    
    const dataTypes = useDataTypes();
    const fieldTypes = useFieldTypes();

    const { isReseller } = useSecurity();

    useEffect(() => {
        if (nameFieldBackendError) {
            setNameError(nameFieldBackendError);
        }
    }, [nameFieldBackendError])

    const handleChangeFieldType = (fieldType: string): void => {
        const customField = getCustomFieldTypeOptions(fieldType);
        setField((field) => ({ ...field, ...customField, type: fieldType, isCustom: fieldType === CustomFieldTypes.Custom, isLengthError: false, isLengthSetable: customField.FieldType === ContactFieldTypes.Text }));
    };

    const handleSetCustomFields = (fieldType: ContactFieldType): void => {
        const length = fieldType !== ContactFieldType.Text ? 0 : getCustomFieldTypeOptions(field.type).Length;
        const backendFieldType = fieldType as any as ContactFieldTypes;
        setField((field) => ({ ...field, FieldType: backendFieldType, Length: length, isLengthError: false, isLengthSetable: backendFieldType === ContactFieldTypes.Text }));
    };

    const handleFieldLengthChange = (event): void => {
        const length = event.target.value ?? event.currentTarget.value;
        const newLength = length < CUSTOM_FIELD_MAX_FIELD_LENGTH ? length : CUSTOM_FIELD_MAX_FIELD_LENGTH;
        setField((field) => ({ ...field, Length: newLength, isLengthError: !length || length < 1 }));
    };

    const handleNameChange = (event): void => {
        const newName = event.currentTarget.value;
        const error = validateCustomFieldName(newName, collection); 
        setNameError(error);
        setField((field) => ({ ...field, isNameError: !!error, Name: newName}));
    }

    useEffect(() => { onChange(field); }, [field]);

    return (
        <>
            <InfoWrapper>
                <Typography variant="body1">
                    {t('managecontactfields')}{' '}
                    {!isReseller && (
                        <Link
                            target="_blank"
                            style={{ color: palette.indigo[6] }}
                            href={links.mergeTags}
                        >
                            {t('learnmore')}
                        </Link>
                    )}
                </Typography>
            </InfoWrapper>
            <InputsWrapper columns={field.FieldType === ContactFieldTypes.Text ? 4 : 3}>
                <SimplePopover
                    id="modal-field-type-input"
                    label={t('fieldtype')}
                    options={dataTypes}
                    defaultPlaceholder={t('choosefieldtype')}
                    type="input"
                    fullWidth
                    value={field.type}
                    onItemClick={(e, obj): void => {
                        handleChangeFieldType(obj.value as string);
                    }}
                    placement="bottom-start"
                    popperProps={{ 
                        modifiers: {
                            preventOverflow: {
                                boundariesElement: 'viewport',
                                padding: 16,
                            },
                        }
                    }}
                />
                <SimplePopover
                    id="modal-data-type-input"
                    label={t('datatype')}
                    options={fieldTypes}
                    type="input"
                    fullWidth
                    disabled={!field.isCustom}
                    value={field.FieldType}
                    onItemClick={(e, obj): void => {
                        handleSetCustomFields(obj.value as ContactFieldType);
                    }}
                    placement="bottom-start"
                    popperProps={{ 
                        modifiers: {
                            preventOverflow: {
                                boundariesElement: 'viewport',
                                padding: 16,
                            },
                        }
                    }}
                />
                {field.FieldType === ContactFieldTypes.Text && <div>
                    <NumberInput
                        id="modal-field-length-input"
                        name="NewFieldLength"
                        value={field.Length}
                        min={1}
                        max={CUSTOM_FIELD_MAX_FIELD_LENGTH}
                        step={1}
                        label={t('fieldlength')}
                        onClickButton={handleFieldLengthChange}
                        onChange={handleFieldLengthChange}
                        error={field.isLengthError}
                    />
                </div>}
                <div>
                    <BasicInput
                        id="modal-field-name-input"
                        label={t('fieldname')}
                        type="input"
                        value={field?.Name || ''}
                        error={!!nameError}
                        errorMessage={nameError}
                        onChange={handleNameChange}
                        autoComplete="off"
                    />
                </div>
            </InputsWrapper>
        </>
    );
};
